import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

export default function AuthModal({ onClose }) {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("login"); // "login" | "register"
  const [fields, setFields] = useState({ username: "", email: "", password: "", confirm: "" });
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);

  const set = (k) => (e) => setFields((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (tab === "register" && fields.password !== fields.confirm) {
      return setError("Passwords do not match.");
    }

    if (tab === "register" && !agreedToTerms) {
      return setError("You must agree to the Terms of Service and Privacy Policy to create an account.");
    }

    setSubmitting(true);
    try {
      if (tab === "login") {
        await login(fields.email, fields.password);
      } else {
        await register(fields.email, fields.password, fields.username);
      }
      onClose();
      // If there's a pending trip waiting, send user to /upgrade to complete the flow
      if (localStorage.getItem("pendingTrip")) {
        navigate("/upgrade", { state: { fromApp: true } });
      }
    } catch (err) {
      setError(
        err.response?.data?.error || err.message || "Something went wrong."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    // Backdrop
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="w-full max-w-sm bg-white rounded-2xl shadow-xl p-6">
        {/* Tabs */}
        <div className="flex mb-6 border-b border-gray-200">
          {["login", "register"].map((t) => (
            <button
              key={t}
              onClick={() => { setTab(t); setError(null); setAgreedToTerms(false); }}
              className={`flex-1 pb-2 text-sm font-semibold capitalize transition border-b-2 -mb-px ${
                tab === t
                  ? "border-primary text-primary"
                  : "border-transparent text-gray-400 hover:text-primary"
              }`}
            >
              {t === "login" ? "Log In" : "Register"}
            </button>
          ))}
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {tab === "register" && (
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Username</label>
              <input
                type="text"
                required
                value={fields.username}
                onChange={set("username")}
                className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent"
                placeholder="your_username"
              />
            </div>
          )}

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Email</label>
            <input
              type="email"
              required
              value={fields.email}
              onChange={set("email")}
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent"
              placeholder="you@example.com"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Password</label>
            <input
              type="password"
              required
              value={fields.password}
              onChange={set("password")}
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent"
              placeholder="••••••••"
            />
          </div>

          {tab === "login" && (
            <div className="text-right -mt-2">
              <a
                href="https://routemuse.me/wp-login.php?action=lostpassword"
                target="_blank"
                rel="noreferrer"
                className="text-xs text-gray-400 hover:text-primary transition"
              >
                Forgot password?
              </a>
            </div>
          )}

          {tab === "register" && (
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Confirm Password</label>
              <input
                type="password"
                required
                value={fields.confirm}
                onChange={set("confirm")}
                className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent"
                placeholder="••••••••"
              />
            </div>
          )}

          {tab === "register" && (
            <div className="flex items-start gap-2">
              <input
                type="checkbox"
                id="terms"
                checked={agreedToTerms}
                onChange={(e) => setAgreedToTerms(e.target.checked)}
                className="mt-0.5 accent-primary"
              />
              <label htmlFor="terms" className="text-xs text-gray-500 leading-relaxed">
                I agree to the{" "}
                <a
                  href="https://routemuse.me/terms-of-service/"
                  target="_blank"
                  rel="noreferrer"
                  className="text-primary underline hover:text-accent"
                >
                  Terms of Service
                </a>{" "}
                and{" "}
                <a
                  href="https://routemuse.me/privacy-policy/"
                  target="_blank"
                  rel="noreferrer"
                  className="text-primary underline hover:text-accent"
                >
                  Privacy Policy
                </a>
              </label>
            </div>
          )}

          {error && (
            <p className="text-sm text-red-500 bg-red-50 rounded-lg px-3 py-2">{error}</p>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="w-full py-3 rounded-xl bg-primary text-white font-semibold text-sm hover:bg-accent transition disabled:opacity-50"
          >
            {submitting
              ? "Please wait…"
              : tab === "login"
              ? "Log In"
              : "Create Account"}
          </button>
        </form>

        <button
          onClick={onClose}
          className="mt-4 w-full text-center text-xs text-gray-400 hover:text-gray-600 transition"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
